import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SMSTextarea } from "@/components/SMSTextarea";
import { Loader2 } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import {
  RecipientsFilter,
  type FilterItem,
  type FilterProductOption,
} from "./RecipientsFilter";
import {
  apiFilterToFilterItem,
  DEFAULT_FILTERS_OPERATOR,
  createDefaultSmsRecipientFilters,
  filterItemToApiFilter,
  type FiltersOperator,
} from "@/lib/contactFilterApi";
import { estimateSmsCampaignFanrankCost } from "@/utils/smsUtils";
import {
  leadMatchesCsvPhone,
  parsePhoneNumbersFromCsv,
} from "@/lib/parsePhoneNumbersFromCsv";

function dateToDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function MessagesSMSCompose() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [campaignTitle, setCampaignTitle] = useState("");
  const [textMessage, setTextMessage] = useState("");
  const [trackLinks, setTrackLinks] = useState(false);
  const [sendAtLocal, setSendAtLocal] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState<string | undefined>();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const loadedSelectedLeadIdsRef = useRef<string[] | null>(null);

  const [filters, setFilters] = useState<FilterItem[]>(() => createDefaultSmsRecipientFilters());
  const [filtersOperator, setFiltersOperator] = useState<FiltersOperator>(DEFAULT_FILTERS_OPERATOR);
  const [pages, setPages] = useState<{ _id: string; title: string; route: string }[]>([]);
  const [groups, setGroups] = useState<{ _id: string; name: string; contacts?: number }[]>([]);
  const [products, setProducts] = useState<FilterProductOption[]>([]);

  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [leads, setLeads] = useState<
    { _id: string; firstName?: string; lastName?: string; phoneNumber?: string }[]
  >([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(true);
  const [recipientQueryReady, setRecipientQueryReady] = useState(() => !id);
  const [csvPhoneNumbers, setCsvPhoneNumbers] = useState<Set<string> | null>(null);
  const [csvSkippedCount, setCsvSkippedCount] = useState(0);
  const [isParsingPhoneCsv, setIsParsingPhoneCsv] = useState(false);

  const applyLeadSelection = useCallback(
    (
      leadList: { _id: string; phoneNumber?: string }[],
      csvPhones: Set<string> | null,
    ) => {
      if (leadList.length === 0) {
        return new Set<string>();
      }
      if (loadedSelectedLeadIdsRef.current) {
        const validIds = new Set(leadList.map((l) => l._id));
        return new Set(
          loadedSelectedLeadIdsRef.current.filter((lid) => validIds.has(lid)),
        );
      }
      if (csvPhones && csvPhones.size > 0) {
        return new Set(
          leadList
            .filter((lead) =>
              leadMatchesCsvPhone({ leadPhone: lead.phoneNumber, csvPhones }),
            )
            .map((l) => l._id),
        );
      }
      return new Set(leadList.map((l) => l._id));
    },
    [],
  );

  const handleSelectLead = useCallback((leadId: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (leads.length === 0) return;
    const allSelected = leads.every((l) => selectedLeadIds.has(l._id));
    setSelectedLeadIds(allSelected ? new Set() : new Set(leads.map((l) => l._id)));
  }, [leads, selectedLeadIds]);

  const hasPageFilter = filters.some((f) => f.filterType === "page");
  const hasGroupFilter = filters.some((f) => f.filterType === "contactGroup");
  const hasProductFilter = filters.some((f) => f.filterType === "product");
  useEffect(() => {
    if (hasPageFilter) {
      fetch("/api/pages")
        .then((r) => r.json())
        .then((data) => setPages(data.pages || []))
        .catch(() => setPages([]));
    }
  }, [hasPageFilter]);
  useEffect(() => {
    if (hasGroupFilter) {
      fetch("/api/lead-groups")
        .then((r) => r.json())
        .then((data) => setGroups(data.groups || []))
        .catch(() => setGroups([]));
    }
  }, [hasGroupFilter]);
  useEffect(() => {
    if (hasProductFilter) {
      fetch("/api/products")
        .then((r) => r.json())
        .then((data) => setProducts(data.products || []))
        .catch(() => setProducts([]));
    }
  }, [hasProductFilter]);

  useEffect(() => {
    if (!recipientQueryReady) {
      return;
    }

    const ac = new AbortController();

    const fetchRecipientCount = async () => {
      const apiFilters = filters
        .map(filterItemToApiFilter)
        .filter((f): f is Record<string, unknown> => f != null);

      const shouldListRecipients = apiFilters.length > 0 || filters.length === 0;

      setIsLoadingRecipients(true);
      try {
        const filtersParam = encodeURIComponent(JSON.stringify(apiFilters));
        const listParam = shouldListRecipients ? "&list=true" : "";
        const operatorParam =
          filtersOperator !== "and" ? `&filtersOperator=${filtersOperator}` : "";
        const response = await fetch(
          `/api/contacts/recipients?filters=${filtersParam}${listParam}${operatorParam}`,
          { signal: ac.signal },
        );
        if (response.ok) {
          const data = await response.json();
          if (ac.signal.aborted) {
            return;
          }
          const leadList = data.leads || [];
          setRecipientCount(data.count);
          setLeads(leadList);
          setSelectedLeadIds(applyLeadSelection(leadList, csvPhoneNumbers));
          if (loadedSelectedLeadIdsRef.current) {
            loadedSelectedLeadIdsRef.current = null;
          }
        } else {
          if (!ac.signal.aborted) {
            setRecipientCount(0);
            setLeads([]);
            setSelectedLeadIds(new Set());
          }
        }
      } catch (error) {
        if (ac.signal.aborted) {
          return;
        }
        console.error("Error fetching recipients:", error);
        setRecipientCount(0);
        setLeads([]);
        setSelectedLeadIds(new Set());
      } finally {
        if (!ac.signal.aborted) {
          setIsLoadingRecipients(false);
        }
      }
    };

    void fetchRecipientCount();

    return () => ac.abort();
  }, [filters, filtersOperator, recipientQueryReady, csvPhoneNumbers, applyLeadSelection]);

  const csvMatchedCount = useMemo(() => {
    if (!csvPhoneNumbers || csvPhoneNumbers.size === 0) return 0;
    return leads.filter((lead) =>
      leadMatchesCsvPhone({ leadPhone: lead.phoneNumber, csvPhones: csvPhoneNumbers }),
    ).length;
  }, [leads, csvPhoneNumbers]);

  const handleUploadPhoneCsv = useCallback(async (file: File) => {
    setIsParsingPhoneCsv(true);
    try {
      const text = await file.text();
      const { phones, parsedCount, skippedCount } = parsePhoneNumbersFromCsv(text);
      if (parsedCount === 0) {
        alert("No valid phone numbers found in CSV.");
        return;
      }
      setCsvSkippedCount(skippedCount);
      setCsvPhoneNumbers(phones);
    } catch (error) {
      console.error("Error parsing phone CSV:", error);
      alert("Could not read that CSV file.");
    } finally {
      setIsParsingPhoneCsv(false);
    }
  }, []);

  const handleClearPhoneCsv = useCallback(() => {
    setCsvPhoneNumbers(null);
    setCsvSkippedCount(0);
    setSelectedLeadIds(new Set(leads.map((l) => l._id)));
  }, [leads]);

  useEffect(() => {
    if (!id) {
      setRecipientQueryReady(true);
      loadedSelectedLeadIdsRef.current = null;
      setCampaignStatus(undefined);
      setFilters(createDefaultSmsRecipientFilters());
      setFiltersOperator(DEFAULT_FILTERS_OPERATOR);
      setCsvPhoneNumbers(null);
      setCsvSkippedCount(0);
      return;
    }

    setRecipientQueryReady(false);
    setIsLoading(true);
    loadedSelectedLeadIdsRef.current = null;

    fetch(`/api/campaigns/${id}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.campaign?.workerStatus === "processing") {
          navigate(`/messages/sms/campaigns/${id}/messages`, { replace: true });
          return;
        }
        if (data.campaign) {
          setCampaignStatus(data.campaign.status);
          setCampaignTitle(data.campaign.name || "");
          setTextMessage(data.campaign.body || "");
          setTrackLinks(Boolean(data.campaign.trackLinks));
          if (data.campaign.sendAt) {
            setSendAtLocal(dateToDatetimeLocalValue(new Date(data.campaign.sendAt)));
          } else {
            setSendAtLocal("");
          }

          if (
            data.campaign.filters &&
            Array.isArray(data.campaign.filters) &&
            data.campaign.filters.length > 0
          ) {
            setFilters(
              data.campaign.filters.map((f: Record<string, unknown>, i: number) =>
                apiFilterToFilterItem(f, `loaded-${i}-${crypto.randomUUID()}`),
              ),
            );
          } else {
            setFilters(createDefaultSmsRecipientFilters());
          }
          if (data.campaign.filtersOperator === "or") {
            setFiltersOperator("or");
          }
          if (data.campaign.selectedLeadIds?.length) {
            loadedSelectedLeadIdsRef.current = data.campaign.selectedLeadIds.map(
              (x: { toString: () => string }) => x.toString(),
            );
          } else {
            loadedSelectedLeadIdsRef.current = null;
          }
        }
      })
      .catch((error) => {
        console.error("Error loading campaign:", error);
      })
      .finally(() => {
        setIsLoading(false);
        setRecipientQueryReady(true);
      });
  }, [id, navigate]);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Messages", path: "/messages" },
      { label: "SMS", path: "/messages/sms/campaigns" },
      { label: "Campaigns", path: "/messages/sms/campaigns" },
      {
        label: id ? campaignTitle || "Edit" : "Compose",
        path: id ? `/messages/sms/campaigns/${id}` : "/messages/sms/compose",
      },
    ]);
    return () => clearBreadcrumbs();
  }, [id, campaignTitle]);

  const selectedLeads = useMemo(
    () => leads.filter((l) => selectedLeadIds.has(l._id)),
    [leads, selectedLeadIds],
  );

  const smsCostEstimate = useMemo(
    () =>
      estimateSmsCampaignFanrankCost({
        templateBody: textMessage,
        leads: selectedLeads,
      }),
    [textMessage, selectedLeads],
  );

  const handleNext = async () => {
    if (!campaignTitle.trim() || !textMessage.trim()) {
      alert("Please fill in both Campaign Title and Text Message");
      return;
    }

    setIsSaving(true);
    try {
      const filtersArray = filters
        .map(filterItemToApiFilter)
        .filter((f): f is Record<string, unknown> => f != null);

      const selectedIdsPayload =
        selectedLeadIds.size === 0
          ? []
          : selectedLeadIds.size === leads.length
            ? null
            : Array.from(selectedLeadIds);

      const sendAt =
        sendAtLocal.trim() === ""
          ? null
          : new Date(sendAtLocal).toISOString();

      const payload = {
        name: campaignTitle,
        body: textMessage,
        status: "draft",
        trackLinks,
        sendAt,
        filters: filtersArray.length > 0 ? filtersArray : undefined,
        filtersOperator,
        ...(id ? { selectedLeadIds: selectedIdsPayload } : {}),
      };

      const url = id ? `/api/campaigns/${id}` : "/api/campaigns";
      const method = id ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save campaign");
      }

      navigate("/messages/sms/campaigns");
    } catch (error) {
      console.error("Error saving campaign:", error);
      alert(error instanceof Error ? error.message : "Failed to save campaign");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto w-full">
      <div className="space-y-6 w-full max-w-full">
        <div>
          <h1 className="text-2xl font-bold mb-6">
            {campaignStatus === "queued" ? "Edit queued SMS campaign" : "Compose SMS Campaign"}
          </h1>
        </div>

        <div className="space-y-2">
          <Label htmlFor="campaign-title">Campaign Title</Label>
          <Input
            id="campaign-title"
            value={campaignTitle}
            onChange={(e) => setCampaignTitle(e.target.value)}
            placeholder="Enter campaign title"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="text-message">Text Message</Label>
          <SMSTextarea
            value={textMessage}
            onChange={setTextMessage}
            placeholder="Enter SMS message body..."
          />
          <div className="flex items-center space-x-2 pt-1">
            <Checkbox
              id="track-links"
              checked={trackLinks}
              onCheckedChange={(checked) => setTrackLinks(checked === true)}
            />
            <Label htmlFor="track-links" className="font-normal cursor-pointer">
              Track link clicks
            </Label>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="send-at">Send At</Label>
          <Input
            id="send-at"
            type="datetime-local"
            value={sendAtLocal}
            onChange={(e) => setSendAtLocal(e.target.value)}
          />
          <p className="text-sm text-muted-foreground">
            Optional. Leave empty to send as soon as possible.
          </p>
        </div>

        <RecipientsFilter
          filters={filters}
          onFiltersChange={setFilters}
          filtersOperator={filtersOperator}
          onFiltersOperatorChange={setFiltersOperator}
          pages={pages}
          groups={groups}
          products={products}
          leads={leads}
          selectedLeadIds={selectedLeadIds}
          onSelectLead={handleSelectLead}
          onSelectAll={handleSelectAll}
          csvPhoneUpload={{
            uploadedCount: csvPhoneNumbers?.size ?? 0,
            matchedCount: csvMatchedCount,
            skippedCount: csvSkippedCount,
            isParsing: isParsingPhoneCsv,
            onUpload: handleUploadPhoneCsv,
            onClear: handleClearPhoneCsv,
          }}
        />

        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <p className="text-sm text-muted-foreground">
              {isLoadingRecipients ? (
                <>
                  <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
                  Calculating recipients...
                </>
              ) : (
                <>
                  {recipientCount !== null
                    ? leads.length > 0
                      ? selectedLeadIds.size !== recipientCount
                        ? `Recipients: ${selectedLeadIds.size.toLocaleString()} of ${recipientCount.toLocaleString()} selected`
                        : `Recipients: ${selectedLeadIds.size.toLocaleString()}`
                      : `Recipients: ${recipientCount.toLocaleString()}`
                    : "Recipients: 0"}
                </>
              )}
            </p>
            {!isLoadingRecipients &&
            textMessage.trim() &&
            leads.length > 0 &&
            selectedLeadIds.size > 0 ? (
              smsCostEstimate.billedRecipientCount > 0 ? (
                <p className="text-sm text-muted-foreground">
                  SMS cost:{" "}
                  <span className="font-medium text-foreground">
                    $
                    {(smsCostEstimate.totalCents / 100).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  {" · "}
                  {smsCostEstimate.totalSegmentsAcrossRecipients.toLocaleString()} segment
                  {smsCostEstimate.totalSegmentsAcrossRecipients !== 1 ? "s" : ""} total
                  {" · "}
                  {smsCostEstimate.billedRecipientCount.toLocaleString()} recipient
                  {smsCostEstimate.billedRecipientCount !== 1 ? "s" : ""} in estimate
                  {smsCostEstimate.skippedNoPhoneOrRate > 0
                    ? ` · ${smsCostEstimate.skippedNoPhoneOrRate.toLocaleString()} excluded (missing number or unsupported destination)`
                    : ""}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  SMS cost:{" "}
                  <span className="font-medium text-foreground">$0.00</span>
                  {" — "}
                  selected recipients have no US/CA numbers in this cost estimate.
                  {smsCostEstimate.skippedNoPhoneOrRate > 0
                    ? ` (${smsCostEstimate.skippedNoPhoneOrRate.toLocaleString()} excluded)`
                    : ""}
                </p>
              )
            ) : null}
          </div>
          <Button
            onClick={handleNext}
            disabled={
              isSaving ||
              !campaignTitle.trim() ||
              !textMessage.trim() ||
              (leads.length > 0 && selectedLeadIds.size === 0)
            }
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : sendAtLocal.trim() !== "" ? (
              "Schedule"
            ) : (
              "Send"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
